import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    PersonalData,
    PersonalDataValidation,
} from "../../../../types/bookingCreation";
import { Input } from "components/ui/input";
import PhoneNumberInput from "components/ui/phone-input";
import { RootState } from "../../../../redux/store";
import {
    landlordEmailChanged,
    landlordFirstNameChanged,
    landlordLastNameChanged,
    landlordPhoneChanged,
    setNextEnabled,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useKeyHandoverStepCounter } from "../hooks/useKeyHandoverStepCounter";
import { useEmailFieldFeedback } from "../../common/hooks/useEmailFieldFeedback";

const KeyHandoverHostDetailsStep = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { current, total } = useKeyHandoverStepCounter("host");
    const landlordData: PersonalData = useSelector(
        (state: RootState) => state.bookingCreation.landlordData
    );
    const landlordDataValidation: PersonalDataValidation = useSelector(
        (state: RootState) => state.bookingCreation.landlordDataValidation
    );

    // Track touched state to show validation feedback only after user interaction
    const [touched, setTouched] = React.useState({
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
    });

    const markTouched = (field: keyof typeof touched) =>
        setTouched((prev) => ({ ...prev, [field]: true }));

    const onFirstNameChange = (firstName: string) =>
        dispatch(landlordFirstNameChanged({ firstName }));
    const onLastNameChange = (lastName: string) =>
        dispatch(landlordLastNameChanged({ lastName }));
    const onPhoneChange = (phone: string) =>
        dispatch(landlordPhoneChanged({ phone }));
    const onEmailChange = (email: string) =>
        dispatch(landlordEmailChanged({ email }));

    const emailFeedback = useEmailFieldFeedback(landlordData.email, touched.email);

    // Keep the global "Next" button disabled until the host details are valid
    React.useEffect(() => {
        const valid = Boolean(
            landlordDataValidation.firstName &&
                landlordDataValidation.lastName &&
                landlordDataValidation.email &&
                landlordDataValidation.phone
        );
        dispatch(setNextEnabled(valid));
    }, [
        dispatch,
        landlordDataValidation.firstName,
        landlordDataValidation.lastName,
        landlordDataValidation.email,
        landlordDataValidation.phone,
    ]);

    return (
        <div className="w-full flex flex-col items-center">
            {/* Sub headline: Step indicator under main header */}
            <p className="-mt-5 mb-6 text-xs text-muted-foreground text-center w-full" role="status" aria-live="polite">
                {t("book_location.keys.step_counter", {
                    current,
                    total,
                    defaultValue: "Schritt {{current}} von {{total}}",
                })}
            </p>
            <div className="w-full max-w-md mx-auto space-y-4 pb-4">
                <div className="space-y-1">
                    <label htmlFor="landlord-first-name" className="text-sm font-medium">
                        {t("book_location.personal_data.first_name")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <Input
                        id="landlord-first-name"
                        value={landlordData.firstName}
                        onChange={(e) => onFirstNameChange(e.target.value)}
                        onBlur={() => markTouched("firstName")}
                        autoComplete="given-name"
                        required
                        error={touched.firstName && !landlordDataValidation.firstName}
                        aria-invalid={
                            touched.firstName && !landlordDataValidation.firstName
                        }
                        aria-describedby="landlord-first-name-error"
                    />
                    {touched.firstName && !landlordDataValidation.firstName && (
                        <p
                            id="landlord-first-name-error"
                            className="text-sm text-appRed mt-1"
                        >
                            {t("book_location.personal_data.validation.first_name_required", {
                                defaultValue: "Bitte gib deinen Vornamen ein.",
                            })}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <label htmlFor="landlord-last-name" className="text-sm font-medium">
                        {t("book_location.personal_data.last_name")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <Input
                        id="landlord-last-name"
                        value={landlordData.lastName}
                        onChange={(e) => onLastNameChange(e.target.value)}
                        onBlur={() => markTouched("lastName")}
                        autoComplete="family-name"
                        required
                        error={touched.lastName && !landlordDataValidation.lastName}
                        aria-invalid={touched.lastName && !landlordDataValidation.lastName}
                        aria-describedby="landlord-last-name-error"
                    />
                    {touched.lastName && !landlordDataValidation.lastName && (
                        <p
                            id="landlord-last-name-error"
                            className="text-sm text-appRed mt-1"
                        >
                            {t("book_location.personal_data.validation.last_name_required", {
                                defaultValue: "Bitte gib deinen Nachnamen ein.",
                            })}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <label htmlFor="landlord-email" className="text-sm font-medium">
                        {t("book_location.personal_data.email")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <Input
                        id="landlord-email"
                        type="email"
                        value={landlordData.email}
                        onChange={(e) => onEmailChange(e.target.value)}
                        onBlur={() => markTouched("email")}
                        autoComplete="email"
                        required
                        error={!!emailFeedback.error}
                        aria-invalid={!!emailFeedback.error}
                        aria-describedby={
                            emailFeedback.error
                                ? "landlord-email-error"
                                : emailFeedback.warning
                                ? "landlord-email-warning"
                                : undefined
                        }
                    />
                    {emailFeedback.error && (
                        <p id="landlord-email-error" className="text-sm text-appRed mt-1">
                            {emailFeedback.error}
                        </p>
                    )}
                    {emailFeedback.warning && (
                        <p id="landlord-email-warning" className="text-sm text-slate-600 mt-1">
                            {emailFeedback.warning}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <label htmlFor="landlord-phone" className="text-sm font-medium">
                        {t("book_location.personal_data.phone_number")}<span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                    </label>
                    <PhoneNumberInput
                        id="landlord-phone"
                        value={landlordData.phone}
                        onChange={(val) => onPhoneChange(val)}
                        onBlur={() => markTouched("phone")}
                        autoComplete="tel"
                        error={touched.phone && !landlordDataValidation.phone}
                        aria-invalid={touched.phone && !landlordDataValidation.phone}
                        aria-describedby="landlord-phone-error"
                    />
                    {touched.phone && !landlordDataValidation.phone && (
                        <p id="landlord-phone-error" className="text-sm text-appRed mt-1">
                            {t("book_location.personal_data.validation.phone_invalid", {
                                defaultValue: "Bitte gib eine gültige Telefonnummer ein.",
                            })}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default KeyHandoverHostDetailsStep;
